import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import {
  DropdownModule,
  DropdownMenuData,
  DropdownMenuItem,
} from "@vg-constellation/angular-17/dropdown";
import { InputComponent } from "@vg-constellation/angular-17/input";
import { LabelComponent } from "@vg-constellation/angular-17/label";
import { IconModule } from "@vg-constellation/angular-17/icon";
import { IconButtonComponent } from "@vg-constellation/angular-17/button";
import { LinkComponent } from "@vg-constellation/angular-17/link";
import { CardComponent } from "@vg-constellation/angular-17/card";
import { IMaskModule } from "angular-imask";

import {
  Beneficiary,
  BeneficiarySectionType,
  DropdownStateChangeEvent,
  getCardDensity,
  getBeneficiaryDisplayName,
} from "@models/beneficiary.model";
import { BeneficiaryType } from "../../types/BeneficiaryType";
import { Trust } from "@models/estateProfile.model";
import { Person } from "@services/zok";
import { WebLogger } from "@weps/shared-http";
import { DropdownTypeaheadDirective } from "@weps/shared-ui";
import * as content from "@services/content/guided-flow/account-details-content";
import {
  BENEFICIARY_CARD_TYPE_CONFIGS,
  BeneficiaryCardContext,
  BeneficiaryCardTypeConfig,
} from "./beneficiary-card.config";

@Component({
  selector: "app-beneficiary-card",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DropdownModule,
    InputComponent,
    LabelComponent,
    IconModule,
    IconButtonComponent,
    LinkComponent,
    CardComponent,
    IMaskModule,
    DropdownTypeaheadDirective,
  ],
  templateUrl: "./beneficiary-card.component.html",
  styleUrls: ["./beneficiary-card.component.scss"],
})
export class BeneficiaryCardComponent implements OnInit, OnChanges {
  @Input() beneficiary!: Beneficiary;
  @Input() section: BeneficiarySectionType = "PRIMARY";
  @Input() householdMembers: DropdownMenuItem[] = [];
  @Input() householdPeople: Person[] = [];
  @Input() trusts: Trust[] = [];

  @Output() edit = new EventEmitter<Beneficiary>();
  @Output() remove = new EventEmitter<Beneficiary>();
  @Output() distributionChange = new EventEmitter<{ beneficiary: Beneficiary; value: number }>();
  @Output() personChange = new EventEmitter<{ beneficiary: Beneficiary; memberId: string }>();
  @Output() trustChange = new EventEmitter<{ beneficiary: Beneficiary; memberId: string }>();

  protected readonly content = content;
  protected readonly BeneficiaryType = BeneficiaryType;

  /**
   * Custom imask configuration for distribution percentage input.
   * Enforces: integers only, 0-100 range, auto-appended % suffix.
   */
  protected readonly distributionMask = {
    mask: "VALUE%",
    lazy: false,
    blocks: {
      VALUE: {
        mask: Number,
        scale: 0,
        signed: false,
        min: 0,
        max: 100,
        thousandsSeparator: "",
        padFractionalZeros: false,
        radix: ".",
      },
    },
  };

  dropdownData: DropdownMenuData = { groups: [] };
  activeId = "";
  cardDensity: "small" | "medium" = "medium";
  dropdownVisible = true;
  distributionValue: string = "";

  get displayName(): string {
    return getBeneficiaryDisplayName(this.beneficiary, this.householdPeople, this.trusts);
  }

  get isTrust(): boolean {
    return this.beneficiary?.beneficiaryType === BeneficiaryType.TRUST;
  }

  get typeConfig(): BeneficiaryCardTypeConfig {
    return BENEFICIARY_CARD_TYPE_CONFIGS[this.beneficiary.beneficiaryType];
  }

  private get cardContext(): BeneficiaryCardContext {
    return {
      beneficiary: this.beneficiary,
      householdMembers: this.householdMembers,
      trusts: this.trusts,
      displayName: this.displayName,
      personChange: this.personChange,
      trustChange: this.trustChange,
    };
  }

  ngOnInit(): void {
    this.buildDropdown();
    this.cardDensity = getCardDensity();
    this.distributionValue = String(this.beneficiary.distributionPercentage);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const hasRelevantChanges =
      (!!changes["householdMembers"] && !changes["householdMembers"].firstChange) ||
      (!!changes["householdPeople"] && !changes["householdPeople"].firstChange) ||
      (!!changes["trusts"] && !changes["trusts"].firstChange);

    if (hasRelevantChanges) {
      this.rerenderDropdown();
    }

    if (changes["beneficiary"] && !changes["beneficiary"].firstChange) {
      this.distributionValue = String(this.beneficiary.distributionPercentage);
    }
  }

  @HostListener("window:resize")
  onResize(): void {
    this.cardDensity = getCardDensity();
  }

  private buildDropdown(): void {
    const config = this.typeConfig;
    const ctx = this.cardContext;
    this.dropdownData = config.buildDropdownData(ctx);
    this.activeId = config.getActiveId(ctx);
  }

  private rerenderDropdown(): void {
    this.dropdownVisible = false;
    setTimeout(() => {
      this.buildDropdown();
      this.dropdownVisible = true;
    });
  }

  handleDropdownChange(event: DropdownStateChangeEvent): void {
    const selectedId = event?.activeItems?.[0];
    if (selectedId && selectedId !== this.activeId) {
      WebLogger.log({
        message: `BeneficiaryCard ${this.beneficiary.beneficiaryType} changed`,
        payload: {
          event_type: `beneficiary_card_${this.beneficiary.beneficiaryType}_changed`,
          previous_member_id: this.activeId,
          new_member_id: selectedId,
        },
      });
      this.activeId = selectedId;
      this.typeConfig.handleChange(event, this.cardContext);
    }
  }

  handleEditClick(): void {
    this.edit.emit(this.beneficiary);
  }

  handleRemoveClick(): void {
    this.remove.emit(this.beneficiary);
  }

  /**
   * Handles distribution percentage changes from the imask input.
   * Emits the distributionChange event with the parsed integer value.
   */
  handleDistributionChange(): void {
    const numValue = parseInt(this.distributionValue, 10) || 0;
    this.distributionChange.emit({ beneficiary: this.beneficiary, value: numValue });
  }
}
